import axios from 'axios';
import cache from '../helpers/redis';
import errorRedirectHandler from '../helpers/errorRedirectHandler';
import { recommendClient, RecommendationsResults, TrendingItemsQuery, TrendingFacetsQuery } from '@algolia/recommend';

import { User } from '../types/api/UsersTypes';
import { SessionState } from '../types/state/reducers/authReducersTypes';
import { QueryParams, Ref } from '../types/api/BasicTypes';
import { GetServerSidePropsContext } from 'next';
import { PagedResponse } from '../types/api/Http';
import { CollectionResponse } from '../types/api/CollectionsIndexTypes';
import { CategoryResponse } from '../types/api/CategoriesIndexTypes';
import { CollectionOwnershipView } from '../types/api/OwnershipTypes';
import { ProductPageServerSideProps } from '../types/pages/products/productsPageTypes';
import { RedirectConfigProps } from '../types/pages/generic/redirectConfig';
import { ServerSideProps } from '../types/pages/generic/serverSideProps';

import { ENV } from '../constants/environments';
import {
  CACHE_ENTRY_DURATION_SECONDS as ALGOLIA_CACHE_ENTRY_DURATION_SECONDS,
  TRENDING_COLLECTIONS_TRESHOLD,
  TRENDING_COLLECTIONS_MAX_RECOMMENDATIONS,
  TRENDING_CATEGORIES_TRESHOLD,
  TRENDING_CATEGORIES_MAX_RECOMMENDATIONS,
} from '../constants/algolia';
import { LIBRARY_IDS, CACHE_KEYS } from '../constants/api';
const { LIBRARY_ID_2D, LIBRARY_ID_3D } = LIBRARY_IDS;
const {
  CATEGORIES_CACHE_KEY_2D,
  CATEGORIES_CACHE_KEY_3D,
  COLLECTIONS_CACHE_KEY_2D,
  COLLECTIONS_CACHE_KEY_3D
} = CACHE_KEYS;

export function productIsPFL(product: { sku: string }): boolean {
  const regExp = new RegExp('PF[0-9]+-?[0-9]+');
  return regExp.test(product.sku);
}

export function productIs3D(product: { sku: string }): boolean {
  const regExp = new RegExp('3D.*');
  return regExp.test(product.sku);
}

const apiUrl: string = `${ENV.api.baseURL}`;
const collectionUrl = `${apiUrl}/collections`;
const searchUrl = `${apiUrl}/products/search`;

const fetchCollectionsFromBackend = async (
  collectionParams: QueryParams,
  user: User,
): Promise<PagedResponse<CollectionResponse>> => {
  let collectionsUrl: string =
    (collectionParams.search as string)?.length > 0 ? searchUrl : collectionUrl;
  // temporarily use both API versions for benchmarking & performance testing
  if (process.env.USE_V2_PRODUCT_ENDPOINTS === 'true') {
    collectionsUrl = collectionUrl.replace('v1', 'v2');
    if (collectionParams && collectionParams.search) {
      collectionParams.search_query = collectionParams.search;
      delete collectionParams.search;
      collectionParams.search_query = `‘${collectionParams.search_query}’`;
    }
  };
  const collectionsRes = await axios.get(collectionsUrl, {
    params: collectionParams,
    headers: {
      authorization: user?.token
    }
  });
  return collectionsRes?.data;
};

const getCollections = async (
  collectionParams: QueryParams,
  session: SessionState,
  library_id: number
): Promise<PagedResponse<CollectionResponse>> => {
  const collectionsUrl: string =
    (collectionParams.search as string)?.length > 0 ? searchUrl : collectionUrl;
  let key: string;
  switch (library_id) {
    case LIBRARY_ID_3D:
      key = COLLECTIONS_CACHE_KEY_3D;
      break;
    case LIBRARY_ID_2D:
    default:
      key = COLLECTIONS_CACHE_KEY_2D;
      break;
  }
  const cacheKey = JSON.stringify({
    user: session?.user?.id,
    url: collectionsUrl,
    params: collectionParams,
    key
  });
  const { fetchCollectionsFromBackend, cache } = productsPageHelpers;
  return await cache(cacheKey, 3600, async () =>
    fetchCollectionsFromBackend(collectionParams, session.user as User)
  );
};

const fetchCategoriesFromBackend = async (library_id: number): Promise<Array<CategoryResponse>> => {
  const categoriesUrl: string = `${apiUrl}/categories?limit=50&library_id=${library_id}`;
  const categoriesRes = await axios.get(categoriesUrl);
  return categoriesRes?.data?.data;
};

const getCategories = async (library_id: number): Promise<Array<CategoryResponse>> => {
  let key: string;
  switch (library_id) {
    case LIBRARY_ID_3D:
      key = CATEGORIES_CACHE_KEY_3D;
      break;
    case LIBRARY_ID_2D:
    default:
      key = CATEGORIES_CACHE_KEY_2D;
      break;
  }
  const { fetchCategoriesFromBackend, cache } = productsPageHelpers;
  return await cache(key, 3600, async () => fetchCategoriesFromBackend(library_id));
};

const fetchOwnershipFromBackend = async (user: User): Promise<Array<CollectionOwnershipView>> => {
  const ownershipUrl: string = `${apiUrl}/collections/ownership`;
  let ownershipRes: any;
  ownershipRes = await axios.get(ownershipUrl, {
    headers: {
      authorization: user?.token
    }
  });
  return ownershipRes?.data?.data;
};

const getOwnerships = async (user: User): Promise<Array<CollectionOwnershipView>> => {
  const { fetchOwnershipFromBackend, cache } = productsPageHelpers;
  return await cache(`avfx_ownership_${user.id}`, 600, async () => fetchOwnershipFromBackend(user));
};

const fetchProductsServersideProps = async (
  ctx: GetServerSidePropsContext,
  session: SessionState,
  library_id: number
): Promise<ServerSideProps<ProductPageServerSideProps> | RedirectConfigProps> => {
  const { fetchCollectionsFromBackend, getCategories, getOwnerships, fetchTrendingCollections, fetchTrendingCategories } = productsPageHelpers;

  let collectionParams: QueryParams = {
    desc: false,
    library_id,
    ...ctx.query
  };

  let categories: Array<CategoryResponse>;
  let collectionsRes: PagedResponse<CollectionResponse>;
  let ownership: { [key: string]: CollectionOwnershipView };
  let trendingCollections: Array<RecommendationsResults>;
  let trendingCategories: Array<RecommendationsResults>;
  let promisesError: any;

  const shouldFetchTrendingCollections = library_id === LIBRARY_ID_2D;
  const shouldFetchTrendingCategories = library_id === LIBRARY_ID_2D;
  const shouldFetchOwnerships = library_id === LIBRARY_ID_3D && session?.user?.id;

  const getCategoriesPromise = getCategories(library_id);
  const getCollectionsPromise = fetchCollectionsFromBackend(collectionParams, session?.user as User);

  const promises: Array<Promise<any>> = [getCategoriesPromise, getCollectionsPromise];
  if (shouldFetchTrendingCollections) {
    const getTrendingCollectionsPromise = fetchTrendingCollections();
    promises.push(getTrendingCollectionsPromise);
  }
  if (shouldFetchTrendingCategories) {
    const getTrendingCategoriesPromise = fetchTrendingCategories();
    promises.push(getTrendingCategoriesPromise);
  }
  if (shouldFetchOwnerships) {
    const getOwnershipsPromise = getOwnerships(session?.user as User);
    promises.push(getOwnershipsPromise);
  }

  await Promise.all(promises)
    .then((response: Array<any>) => {
      categories = response[0];
      collectionsRes = response[1];

      if (shouldFetchOwnerships) ownership = response[2];
      if (shouldFetchTrendingCollections) trendingCollections = response[2];
      if (shouldFetchTrendingCategories) trendingCategories = response[3];
    })
    .catch((error) => {
      promisesError = error;
    });

  if (promisesError) {
    console.log(promisesError)
    return errorRedirectHandler(promisesError);
  }

  let searchResultsCount = collectionsRes.total_count;

  const props: ProductPageServerSideProps = {
    categories: categories || [],
    collections: collectionsRes?.data,
    totalCollections: collectionsRes?.total_count,
    totalPages: collectionsRes?.total_pages,
    currentPage: collectionsRes?.page,
    collectionParams: collectionParams,
    searchResultsCount: searchResultsCount,
    collectionOwnership: ownership || null,
    query_id: collectionsRes?.query_id || '',
    trendingCollections: trendingCollections || null,
    trendingCategories: trendingCategories || null,
  };

  return { props };
};

const fetchTrendingCollections = async (config?: {
  categorySlug?: string,
  essentialsCatalog?: boolean,
  freeProduct?: boolean,
}): Promise<Array<RecommendationsResults>> => {
  const client = recommendClient(
    process.env.ALGOLIA_APP_ID,
    process.env.ALGOLIA_API_KEY,
  );
  const request: TrendingItemsQuery = {
    indexName: process.env.ALGOLIA_COLLECTIONS_INDEX,
    threshold: TRENDING_COLLECTIONS_TRESHOLD,
    maxRecommendations: TRENDING_COLLECTIONS_MAX_RECOMMENDATIONS,
    model: 'trending-items',
  };
  let facetName: string = '';
  let facetValue: string = '';
  let cacheKey = `avfx_trending_collections`;

  if (config?.categorySlug) {
    facetName = 'categories';
    facetValue = config?.categorySlug;
  };
  if (config?.essentialsCatalog) {
    facetName = 'essentials_catalog';
    facetValue = 'true';
  };
  if (config?.freeProduct) {
    facetName = 'free_product';
    facetValue = 'true';
  };

  if (!!facetName && !!facetValue) {
    request.facetName = facetName;
    request.facetValue = facetValue;
    cacheKey += `-${[facetName, facetValue].join('_')}`;
  };

  const recommendations = await cache(cacheKey, ALGOLIA_CACHE_ENTRY_DURATION_SECONDS, async () => {
    const response = await client.getRecommendations({ requests: [ request ] })
    return response;
  }, { useCachingInDevEnv: true });
  return recommendations.results;
}

const fetchTrendingCategories = async (): Promise<Array<RecommendationsResults>> => {
  const client = recommendClient(
    process.env.ALGOLIA_APP_ID,
    process.env.ALGOLIA_API_KEY,
  );
  const request: TrendingFacetsQuery = {
    indexName: process.env.ALGOLIA_COLLECTIONS_INDEX,
    threshold: TRENDING_CATEGORIES_TRESHOLD,
    maxRecommendations: TRENDING_CATEGORIES_MAX_RECOMMENDATIONS,
    model: 'trending-facets',
    facetName: 'categories',
  };
  const cacheKey = `avfx_trending_categories`;
  const recommendations = await cache(cacheKey, ALGOLIA_CACHE_ENTRY_DURATION_SECONDS, async () => {
    const response = await client.getRecommendations({ requests: [ request ] });
    return response;
  }, { useCachingInDevEnv: true });
  recommendations.results[0].hits = recommendations.results[0].hits.slice(0, TRENDING_CATEGORIES_MAX_RECOMMENDATIONS);  
  return recommendations.results;
};

const checkCollectionTrending = async (collection: Ref, libraryId: number): Promise<boolean> => {
  if (libraryId !== LIBRARY_ID_2D) return false;
  try {
    const { fetchTrendingCollections } = productsPageHelpers;
    const trendingCollections = await fetchTrendingCollections();
    if (!trendingCollections || !trendingCollections[0]) return false;
    const hits = trendingCollections[0].hits || [];
    return !!hits.find(hit => parseInt(hit['objectID']) === collection.id);
  }
  catch {
    return false;
  }
}

const productsPageHelpers = {
  fetchProductsServersideProps,
  getCollections,
  getCategories,
  getOwnerships,
  productIsPFL,
  productIs3D,
  fetchCollectionsFromBackend,
  fetchCategoriesFromBackend,
  fetchOwnershipFromBackend,
  cache,
  fetchTrendingCollections,
  fetchTrendingCategories,
  checkCollectionTrending,
};

export default productsPageHelpers;
